import React from "react";
import { Box, Button, Flex, Icon, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { getTooltip } from "utils/getMessages";

import { ScheduledJobProto } from "build-ts/schedule";
import CronBuilder from "components/analyst/CronBuilder";
import WorkflowSectionHeading from "modules/analyst/components/WorkflowSectionHeading";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import CustomModal from "components/modals/CustomModal";

export interface WorkflowDefinitionProps {
  trigger: string;
  analysisInstructions: string;
  reportingInstructions: string;
  cronExpression: string;
  setCronExpression: React.Dispatch<React.SetStateAction<string>>;
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  selectedJobDetails: ScheduledJobProto | undefined;
  onViewExecutionLog?: () => void;
}

const WorkflowDefinition: React.FC<WorkflowDefinitionProps> = ({
  trigger,
  analysisInstructions,
  reportingInstructions,
  cronExpression,
  setCronExpression,
  isActive,
  setIsActive,
  selectedJobDetails,
  onViewExecutionLog,
}) => {
  const { isOpen: isGuidanceOpen, onOpen: onGuidanceOpen, onClose: onGuidanceClose } = useDisclosure();

  return (
    <>
      <Flex direction="column" flex="1" minH={0} h="100%" overflow="hidden" px={2}>
        <Box maxW="900px" mx="auto" w="100%" flex="1" minH={0} overflowY="auto" px={4} py={5}>
          <VStack spacing={6} align="stretch">
            {/* Trigger — read-only */}
            <Box>
              <WorkflowSectionHeading
                compact
                label="Description"
                tooltip={getTooltip("workflow_detail.description.tooltip")}
              />
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={3}
                bg="gray.50"
                overflowY="auto"
                overflowX="auto"
              >
                {trigger.trim() ? (
                  <MarkdownRenderer content={trigger} />
                ) : (
                  <Text fontSize="sm" color="gray.400" mb={0}>
                    No description set.
                  </Text>
                )}
              </Box>
            </Box>

            {/* Instructions — read-only with View guidance action */}
            <Box>
              <WorkflowSectionHeading
                compact
                label="Instructions"
                tooltip={getTooltip("workflow_detail.instructions.tooltip")}
                rightSlot={
                  <Button
                    size="xs"
                    variant="outline"
                    fontWeight="700"
                    leftIcon={<Icon as={AppIcons.locked} boxSize={3} />}
                    onClick={onGuidanceOpen}
                    border="1.5px solid"
                    borderColor="purple.700"
                    _hover={{ bg: "purple.50" }}
                  >
                    View guidance
                  </Button>
                }
              />
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={3}
                bg="gray.50"
                overflowY="auto"
                overflowX="auto"
                minH="280px"
              >
                {reportingInstructions.trim() ? (
                  <MarkdownRenderer content={reportingInstructions} />
                ) : (
                  <Text fontSize="sm" color="gray.400" mb={0}>
                    No instructions set.
                  </Text>
                )}
              </Box>
            </Box>

            {/* Schedule — editable via CronBuilder */}
            <Box>
              <WorkflowSectionHeading
                label="Schedule"
                tooltip={getTooltip("workflow_detail.schedule.tooltip")}
                compact
              />
              <CronBuilder
                cronExpression={cronExpression}
                setCronExpression={setCronExpression}
                isActive={isActive}
                setIsActive={setIsActive}
                jobDetails={selectedJobDetails}
                onViewExecutionLog={onViewExecutionLog}
              />
            </Box>
          </VStack>
        </Box>
      </Flex>

      <CustomModal
        isOpen={isGuidanceOpen}
        onClose={onGuidanceClose}
        title="Internal guidance"
        size="lg"
        maxWidth="720px"
        centerInMainContent
        bodyMaxHeight="60vh"
      >
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" px={4} py={3} bg="gray.50">
          {analysisInstructions.trim() ? (
            <MarkdownRenderer content={analysisInstructions} />
          ) : (
            <Text fontSize="sm" color="gray.400" mb={0}>
              No guidance set.
            </Text>
          )}
        </Box>
      </CustomModal>
    </>
  );
};

export default WorkflowDefinition;
